import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

// All requests send cookies automatically
axios.defaults.withCredentials = true;

// Global response interceptor: handle 429 rate limit errors gracefully
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 429) {
      const msg = error.response.data?.detail || 'Too many requests. Please slow down.';
      // Only show toast if sonner is available; otherwise console warn
      try {
        const { toast } = require('sonner');
        toast.error(msg);
      } catch {
        console.warn('[Rate Limited]', msg);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip - AuthCallback handles session exchange
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Try cookie-based auth first (httpOnly cookie sent automatically)
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      setLoading(false);
      return;
    } catch (err) {
      // Retry once for cold-start backend
      if (err?.code === 'ERR_NETWORK' || err?.response?.status >= 500) {
        try {
          await new Promise(r => setTimeout(r, 2000));
          const retry = await axios.get(`${API}/auth/me`);
          setUser(retry.data);
          setLoading(false);
          return;
        } catch {}
      }
    }

    // Cookie-based auth only — httpOnly cookie sent automatically
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    // No longer store in localStorage — httpOnly cookie handles persistence
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(null);
    setToken(null);
  };

  const updateUser = (data) => setUser(prev => ({ ...prev, ...data }));

  const authHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, authHeaders, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
